import { useMemo } from 'react';
import { Redirect, useGlobalSearchParams } from 'expo-router';

import { useLeagueContext } from '@/contexts/LeagueContext';
import { getLeagueHref, type LeaguePrimarySection } from '@/lib/routes';
import { getCurrentTime } from '@/utils/timeUtils';

export default function LeagueIndexRoute() {
  const context = useLeagueContext();
  const routeParams = useGlobalSearchParams<{
    season?: string | string[];
    week?: string | string[];
  }>();

  const seasonParam = Array.isArray(routeParams.season) ? routeParams.season[0] : routeParams.season;
  const weekParam = Array.isArray(routeParams.week) ? routeParams.week[0] : routeParams.week;

  const currentTimestamp = useMemo(() => getCurrentTime().getTime(), []);

  const defaultSection: LeaguePrimarySection = useMemo(() => {
    if (context.isMarchMadness) {
      const hasTournamentResults =
        (context.mmOverview?.winner?.status === 'final') ||
        (context.mmOverview?.rounds ?? []).some((round: any) => (round.finalGames ?? 0) > 0) ||
        context.mmDraftState?.draft?.status === 'completed';

      return hasTournamentResults ? 'standings' : 'draft';
    }

    const lockedGames = (context.sortedGames ?? []).filter((group: any) => {
      const gameTime = new Date(group.game.gameTime).getTime();
      return gameTime <= currentTimestamp;
    }).length;

    return lockedGames > 0 ? 'scores' : 'picks';
  }, [
    context.isMarchMadness,
    context.mmDraftState?.draft?.status,
    context.mmOverview?.rounds,
    context.mmOverview?.winner?.status,
    context.sortedGames,
    currentTimestamp,
  ]);

  return (
    <Redirect
      href={getLeagueHref(context.id, defaultSection, {
        seasonId: seasonParam ?? null,
        weekId: weekParam ?? null,
      })}
    />
  );
}
